import React, { useState } from 'react';
import { SocialIcon, type SocialProvider } from '@components/SocialIcon';
import { Tooltip } from '@components/Tooltip';
import { IconOpenInNew, IconContentCopy, IconCheck } from '@components/icons';
import styles from './ContactLink.module.scss';

export interface ContactLinkProps {
  /** URL to navigate to */
  href: string;
  /** Display label text */
  label: string;
  /** Social media provider for the icon */
  provider: SocialProvider;
  /** Whether the link is disabled */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
}

export const ContactLink: React.FC<ContactLinkProps> = ({
  href,
  label,
  provider,
  disabled = false,
  className = '',
}) => {
  const [copied, setCopied] = useState(false);

  const copyValue = href.startsWith('mailto:') ? href.slice('mailto:'.length) : href;

  const handleCopy = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    navigator.clipboard.writeText(copyValue).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      },
      () => {
        // Clipboard API unavailable — silently ignore
      }
    );
  };

  if (disabled) {
    return (
      <div className={`${styles.container} ${styles.disabled} ${className}`}>
        <SocialIcon provider={provider} className={styles.icon} />
        <div className={styles.labelWrapper}>
          <Tooltip text={label} position="bottom" className={styles.labelTooltip}>
            <span className={styles.label}>{label}</span>
          </Tooltip>
        </div>
      </div>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`${styles.container} ${className}`}
    >
      <SocialIcon provider={provider} className={styles.icon} />
      <div className={styles.labelWrapper}>
        <Tooltip text={label} position="bottom" className={styles.labelTooltip}>
          <span className={styles.label}>{label}</span>
        </Tooltip>
      </div>
      <span className={styles.actions}>
        <button
          type="button"
          className={styles.copyButton}
          onClick={handleCopy}
          aria-label={copied ? 'Copied' : 'Copy link'}
        >
          {copied ? (
            <IconCheck size={14} color="var(--color-semantic-success)" />
          ) : (
            <IconContentCopy size={14} color="var(--color-text-tertiary)" />
          )}
        </button>
        {!href.startsWith('mailto:') && (
          <IconOpenInNew size={14} className={styles.externalIcon} />
        )}
      </span>
    </a>
  );
};
